<template>
  <div class="pdp-grid">
    <div class="pdp-grid__header">
      <div class="pdp-grid__count">
        <b>{{ tiles.length }}</b> {{ tiles.length === 1 ? 'image' : 'images' }}
        <span
          v-if="generatingCount > 0"
          class="pdp-grid__generating"
        > · {{ generatingCount }} generating</span>
      </div>
      <div class="pdp-grid__actions">
        <button
          v-if="showStop"
          type="button"
          class="pdp-grid__stop"
          @click="act('pdp_grid_stop')"
        >
          <i class="bi bi-x-lg" /> Stop
        </button>
        <button
          type="button"
          class="pdp-grid__btn"
          :disabled="!canCreate"
          @click="act('create_pdp_mixed_layout:false')"
        >
          Create Layout
        </button>
        <button
          v-if="grid.hasAiOrScene"
          type="button"
          class="pdp-grid__btn pdp-grid__btn--primary"
          :disabled="!canCreate"
          @click="act('create_pdp_mixed_layout:true')"
        >
          Create Editable Layout
        </button>
      </div>
    </div>

    <div
      v-if="selectedTile"
      class="pdp-grid__viewer"
    >
      <div
        class="pdp-grid__stage"
        :class="`pdp-grid__stage--${selectedTile.state}`"
      >
        <span
          class="pdp-grid__badge"
          :class="{
            'pdp-grid__badge--gallery': selectedTile.mode === 'gallery',
            'pdp-grid__badge--plain': isUseAsIs(selectedTile),
          }"
        >{{ badgeText(selectedTile) }}</span>

        <div
          v-if="selectedTile.state === 'configuring'"
          class="pdp-grid__configure-cta"
        >
          <i class="bi bi-magic" />
          <span class="pdp-grid__stage-label">{{ selectedTile.label }}</span>
          <button
            type="button"
            class="pdp-grid__stage-btn"
            @click="configOpen = true"
          >
            Set up image
          </button>
        </div>

        <template v-else-if="selectedTile.state === 'done' && selectedTile.url">
          <img
            :src="selectedTile.url"
            :alt="selectedTile.label"
            class="pdp-grid__stage-img"
            @click="openTile(selectedTile)"
          >
          <img
            v-if="selectedTile.overlayUrl"
            :key="selectedTile.overlayUrl"
            :src="selectedTile.overlayUrl"
            class="pdp-grid__stage-overlay"
            alt="Product"
          >
          <div
            v-if="selectedTile._mrhiCallouts && selectedTile._mrhiCallouts.type === 'cambridge'"
            class="pdp-grid__callouts"
          >
            <div class="pdp-grid__callout-flavor">
              {{ selectedTile._mrhiCallouts.flavorText }}
            </div>
            <div class="pdp-grid__callout-count">
              {{ selectedTile._mrhiCallouts.countText }}
            </div>
          </div>
        </template>

        <div
          v-else
          class="pdp-grid__placeholder"
        >
          <i
            class="bi bi-stars"
            :class="{
              'pdp-grid__spark--active': selectedTile.state === 'generating' || selectedTile.state === 'regenerating',
              'pdp-grid__spark--queued': selectedTile.state === 'queued' || selectedTile.state === 'stopped',
            }"
          />
          <span class="pdp-grid__stage-label">{{ selectedTile.label }}</span>
          <span
            v-if="selectedTile.state === 'skipped' || selectedTile.state === 'stopped'"
            class="pdp-grid__stage-state"
          >{{ selectedTile.state === 'stopped' ? 'Stopped' : 'Skipped' }}</span>
        </div>

        <div
          v-if="selectedTile.state === 'error'"
          class="pdp-grid__stage-error"
        >
          Generation failed
          <button
            type="button"
            class="pdp-grid__stage-btn"
            @click="act(`pdp_grid_retry:${selectedTile.rowIndex}`)"
          >
            Retry
          </button>
        </div>

        <div
          v-if="selectedTile.state === 'done' && !grid.stopped"
          class="pdp-grid__stage-tools"
        >
          <button
            type="button"
            class="pdp-grid__stage-download"
            title="Download image"
            @click.stop="downloadTile(selectedTile)"
          >
            <i class="bi bi-download" />
          </button>
          <div class="pdp-grid__stage-actions">
            <div
              v-if="canToggleProductMode(selectedTile)"
              class="pdp-grid__mode"
            >
              <button
                type="button"
                class="pdp-grid__mode-btn"
                title="Layered: regenerates this image with a clean background (no product), then adds the product as a separate, editable layer when you create the layout."
                :class="{ 'is-active': tileProductMode(selectedTile) === 'separate' }"
                @click.stop="act(`pdp_grid_mode:${selectedTile.rowIndex}:separate`)"
              >
                Layered
              </button>
              <button
                type="button"
                class="pdp-grid__mode-btn"
                title="Static: regenerates this image with the product composited in (flat, not an editable layer)."
                :class="{ 'is-active': tileProductMode(selectedTile) === 'integrated' }"
                @click.stop="act(`pdp_grid_mode:${selectedTile.rowIndex}:integrated`)"
              >
                Static
              </button>
            </div>
            <button
              type="button"
              class="pdp-grid__stage-btn"
              @click.stop="act(`pdp_grid_regenerate:${selectedTile.rowIndex}`)"
            >
              <i class="bi bi-arrow-counterclockwise" /> Regenerate
            </button>
            <button
              type="button"
              class="pdp-grid__stage-btn pdp-grid__stage-btn--icon"
              title="Remove this image"
              @click.stop="act(`pdp_grid_skip:${selectedTile.rowIndex}`)"
            >
              <i class="bi bi-x-lg" />
            </button>
          </div>
        </div>
      </div>

      <div class="pdp-grid__strip">
        <div class="pdp-grid__strip-scroll">
          <button
            v-for="(tile, idx) in tiles"
            :key="tile.rowIndex"
            type="button"
            class="pdp-grid__thumb"
            :class="{ 'is-selected': idx === selectedIndex }"
            @click="selectTile(idx)"
          >
            <span
              class="pdp-grid__thumb-box"
              :class="`pdp-grid__thumb-box--${tile.state}`"
            >
              <template v-if="tile.state === 'done' && tile.url">
                <img
                  :src="tile.url"
                  :alt="tile.label"
                  class="pdp-grid__thumb-img"
                >
                <img
                  v-if="tile.overlayUrl"
                  :key="tile.overlayUrl"
                  :src="tile.overlayUrl"
                  class="pdp-grid__thumb-overlay"
                  alt="Product"
                >
              </template>
              <i
                v-else-if="tile.state === 'configuring'"
                class="bi bi-magic pdp-grid__thumb-configuring"
              />
              <i
                v-else-if="tile.state === 'error'"
                class="bi bi-exclamation-triangle pdp-grid__thumb-error"
              />
              <span
                v-else-if="tile.state === 'skipped' || tile.state === 'stopped'"
                class="pdp-grid__thumb-skipped"
              >{{ tile.state === 'stopped' ? 'Stopped' : 'Skipped' }}</span>
              <span
                v-else
                class="pdp-grid__thumb-spinner"
              />
            </span>
            <span class="pdp-grid__thumb-label">{{ tile.label }}</span>
          </button>
        </div>

        <div
          v-if="addableTypes.length"
          class="pdp-grid__thumb pdp-grid__thumb--add"
        >
          <button
            ref="addTrigger"
            type="button"
            class="pdp-grid__thumb-box pdp-grid__add-trigger"
            :class="{ 'is-open': addableTypesOpen }"
            title="Add image type"
            @click="toggleAddDropdown"
          >
            <i class="bi bi-plus-lg" />
          </button>
          <span class="pdp-grid__thumb-label">Add</span>
          <div
            v-if="addableTypesOpen"
            class="pdp-grid__add-dropdown-menu"
            :class="{ 'pdp-grid__add-dropdown-menu--down': dropdownOpensDown }"
          >
            <div class="pdp-grid__add-dropdown-header">
              Add more to this set
            </div>
            <button
              v-for="t in addableTypes"
              :key="t.key"
              type="button"
              class="pdp-grid__add-item"
              @click="addType(t.key)"
            >
              <i class="bi bi-plus-lg" /> {{ t.label }}
            </button>
          </div>
        </div>
      </div>
    </div>


    <!-- Configure panel for an added tile. Rendered as an overlay (not inside the
         image stage) so it is never clipped or squeezed into the tile's aspect
         ratio. (POR-13766-6) -->
    <div
      v-if="configuringTile && configOpen"
      class="pdp-grid__configure-overlay"
      @click.self="configOpen = false"
    >
      <div
        class="pdp-grid__configure"
        @click.stop
      >
        <div class="pdp-grid__configure-head">
          <span class="pdp-grid__configure-title">
            <i class="bi bi-magic" /> New image — {{ configuringTile.label }}
          </span>
          <span class="pdp-grid__configure-optional">All optional</span>
          <button
            type="button"
            class="pdp-grid__configure-close"
            aria-label="Close"
            @click="configOpen = false"
          >
            <i class="bi bi-x-lg" />
          </button>
        </div>
        <label class="pdp-grid__configure-label">Direction</label>
        <textarea
          v-model="configDirection"
          class="pdp-grid__configure-text"
          rows="3"
          placeholder="e.g. &quot;warm afternoon kitchen, soft shadows&quot;"
        />

        <div class="pdp-grid__configure-row">
          <label class="pdp-grid__configure-label">
            <i class="bi bi-file-text" /> Overlay text
          </label>
          <div class="pdp-grid__configure-modes">
            <button
              type="button"
              class="pdp-grid__configure-mode"
              :class="{ 'is-active': configOverlayMode === 'editable' }"
              @click="configOverlayMode = 'editable'"
            >
              Editable
            </button>
            <button
              type="button"
              class="pdp-grid__configure-mode"
              :class="{ 'is-active': configOverlayMode === 'part_of_image' }"
              @click="configOverlayMode = 'part_of_image'"
            >
              Baked in
            </button>
          </div>
        </div>
        <textarea
          v-model="configOverlayText"
          class="pdp-grid__configure-text"
          rows="2"
          placeholder="e.g. &quot;Now 20% off&quot;"
        />

        <div class="pdp-grid__configure-tabs">
          <button
            type="button"
            class="pdp-grid__configure-tab"
            :class="{ 'is-active': configTab === 'products' }"
            @click="configTab = 'products'"
          >
            Product images
            <span
              v-if="configProducts.length"
              class="pdp-grid__configure-tab-dot"
            />
          </button>
          <button
            type="button"
            class="pdp-grid__configure-tab"
            :class="{ 'is-active': configTab === 'refs' }"
            @click="configTab = 'refs'"
          >
            Reference images
            <span
              v-if="configRefs.length"
              class="pdp-grid__configure-tab-dot"
            />
          </button>
          <button
            type="button"
            class="pdp-grid__configure-tab"
            :class="{ 'is-active': configTab === 'logo' }"
            @click="configTab = 'logo'"
          >
            Brand logo
            <span
              v-if="configLogo"
              class="pdp-grid__configure-tab-dot"
            />
          </button>
        </div>

        <div
          v-if="configTabImages.length"
          class="pdp-grid__configure-refs"
        >
          <span
            v-for="(img, imgIdx) in configTabImages"
            :key="`${configuringTile.rowIndex}-${configTab}-${imgIdx}`"
            class="pdp-grid__configure-ref"
          >
            <img
              :src="img"
              :alt="configTabLabel"
            >
            <button
              type="button"
              class="pdp-grid__configure-ref-remove"
              :aria-label="`Remove ${configTabLabel}`"
              @click="removeConfigImage(configTab, imgIdx)"
            >
              <i class="bi bi-x-lg" />
            </button>
          </span>
        </div>
        <div
          class="pdp-grid__configure-drop"
          :class="{ 'is-dragover': configDragSlot === configTab }"
          @dragenter.prevent="configDragSlot = configTab"
          @dragover.prevent="onConfigDragOver(configTab, $event)"
          @dragleave="onConfigDragLeave($event)"
          @drop.prevent.stop="onConfigDrop(configTab, $event)"
        >
          <button
            type="button"
            class="pdp-grid__configure-link"
            @click="pickConfigImagesFromComputer(configTab)"
          >
            <i class="bi bi-upload" /> {{ configUploadLabel }}
          </button>
          <span class="pdp-grid__configure-sep">·</span>
          <button
            type="button"
            class="pdp-grid__configure-link"
            @click="pasteConfigImage(configTab)"
          >
            <i class="bi bi-clipboard" /> Paste
          </button>
          <span class="pdp-grid__configure-sep">·</span>
          <button
            type="button"
            class="pdp-grid__configure-link"
            @click="act(`pdp_grid_configure_${configTabAction}:${configuringTile.rowIndex}`)"
          >
            <i class="bi bi-folder2-open" /> Choose from Files
          </button>
        </div>


        <div class="pdp-grid__configure-foot">
          <button
            type="button"
            class="pdp-grid__configure-generate"
            @click="generateConfigured"
          >
            <i class="bi bi-stars" /> Generate image
          </button>
          <button
            type="button"
            class="pdp-grid__configure-remove"
            @click="removeConfiguredTile"
          >
            Remove
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="lightboxTile"
      class="pdp-grid__lightbox"
      @click.self="closeLightbox"
    >
      <button
        type="button"
        class="pdp-grid__lightbox-download"
        title="Download image"
        @click="downloadLightboxImage"
      >
        <i class="bi bi-download" />
      </button>
      <button
        type="button"
        class="pdp-grid__lightbox-close"
        @click="closeLightbox"
      >
        ✕
      </button>
      <button
        v-if="hasPrev"
        type="button"
        class="pdp-grid__lightbox-nav pdp-grid__lightbox-nav--prev"
        title="Previous image"
        @click="showPrev"
      >
        <i class="bi bi-chevron-left" />
      </button>
      <button
        v-if="hasNext"
        type="button"
        class="pdp-grid__lightbox-nav pdp-grid__lightbox-nav--next"
        title="Next image"
        @click="showNext"
      >
        <i class="bi bi-chevron-right" />
      </button>
      <div class="pdp-grid__lightbox-stage">
        <img
          :src="lightboxTile.url"
          class="pdp-grid__lightbox-img"
          :alt="lightboxTile.label"
        >
        <img
          v-if="lightboxTile.overlayUrl"
          :src="lightboxTile.overlayUrl"
          class="pdp-grid__lightbox-overlay"
          alt="Product"
        >
        <div
          v-if="lightboxTile._mrhiCallouts && lightboxTile._mrhiCallouts.type === 'cambridge'"
          class="pdp-grid__lightbox-callouts"
        >
          <div class="pdp-grid__lightbox-callout-flavor">
            {{ lightboxTile._mrhiCallouts.flavorText }}
          </div>
          <div class="pdp-grid__lightbox-callout-count">
            {{ lightboxTile._mrhiCallouts.countText }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BUILT_IN_TYPES } from '@frontend/store/creative-studio/chat-function-handlers/pdp-generation-paths';
import { downloadFileFromUrl, downloadAttachmentComposite } from '@frontend/utils';

export default {
  name: 'AIChatWidgetPdpParallelGrid',
  props: {
    id: {
      type: String,
      default: 'pdp_parallel_grid',
    },
    grid: {
      type: Object,
      required: true,
    },
    messageIndex: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      selectedIndex: 0,
      lightboxTile: null,
      addableTypesOpen: false,
      dropdownOpensDown: false,
      // Draft config for a tile in the 'configuring' state (POR-13766-6). Seeded
      // from the tile (which the add step pre-filled from the brief / first slot)
      // and sent back on "Generate image".
      configRowIndex: null,
      configOpen: false,
      configDirection: '',
      configOverlayText: '',
      configOverlayMode: 'editable',
      configRefs: [],
      configProducts: [],
      configLogo: null,
      // Which image slot the panel is showing — the three share one tab strip,
      // mirroring the row picker's Customize popover. (POR-13766-6)
      configTab: 'products',
      configDragSlot: null,
    };
  },
  computed: {
    tiles() {
      return (this.grid && this.grid.tiles) || [];
    },
    // The tile shown in the large stage. Clamps a stale selectedIndex (tiles can
    // be added/removed) so the stage never lands on a missing tile. (POR-13766)
    selectedTile() {
      const list = this.tiles;
      if (!list.length) return null;
      const i = Math.min(Math.max(this.selectedIndex, 0), list.length - 1);
      return list[i];
    },
    // The images shown under the active configure tab.
    configTabImages() {
      return this.configList(this.configTab);
    },
    configTabLabel() {
      return { products: 'Product image', refs: 'Reference image', logo: 'Brand logo' }[this.configTab];
    },
    // The brand logo is a single image, so its upload link reads "Replace".
    configUploadLabel() {
      if (!this.configTabImages.length) return 'Upload';
      return this.configTab === 'logo' ? 'Replace' : 'Add more';
    },
    // Action suffix the parent listens for per slot (…_product / …_ref / …_logo).
    configTabAction() {
      return { products: 'product', refs: 'ref', logo: 'logo' }[this.configTab];
    },
    // The tile whose configure panel the overlay edits — only ever the selected
    // one, so the draft state and the panel can't drift apart. (POR-13766-6)
    configuringTile() {
      const tile = this.selectedTile;
      return (tile && tile.state === 'configuring') ? tile : null;
    },
    // Every type stays addable: an added tile now opens a configure panel instead
    // of generating immediately, so a SECOND take on a type the brief already
    // configured is a valid (and pre-filled) choice. (POR-13766-6)
    addableTypes() {
      return Object.values(BUILT_IN_TYPES)
        .slice()
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    generatingCount() {
      return this.tiles.filter((t) => t.state === 'generating' || t.state === 'regenerating').length;
    },
    showStop() {
      return !this.grid.stopped && this.tiles.some((t) => t.state === 'queued' || t.state === 'generating');
    },
    // A tile still being configured has no image yet and is left out of the layout,
    // so it must not hold Create Layout hostage. (POR-13766-6)
    canCreate() {
      const usable = this.tiles.filter((t) => t.state !== 'skipped' && t.state !== 'stopped' && t.state !== 'configuring');
      return usable.length > 0 && usable.every((t) => t.state === 'done');
    },
    // Tiles the lightbox can actually show — same rule openTile uses — in
    // display order. Prev/next steps through these so we never land on a
    // blank/failed/still-generating tile. (POR-13637)
    openableTiles() {
      return this.tiles.filter((t) => t && t.state === 'done' && t.url);
    },
    lightboxIndex() {
      return this.lightboxTile ? this.openableTiles.indexOf(this.lightboxTile) : -1;
    },
    hasPrev() {
      return this.lightboxIndex > 0;
    },
    hasNext() {
      return this.lightboxIndex > -1 && this.lightboxIndex < this.openableTiles.length - 1;
    },
  },
  watch: {
    // A tile added via "+ Add" arrives in the 'configuring' state — jump the stage
    // to it so its panel is what the user sees, instead of leaving the new slot
    // hidden at the end of the filmstrip. (POR-13766-6)
    'grid.tiles.length': function onTileCountChanged(len, prev) {
      if (!len || len <= (prev || 0)) return;
      const lastIdx = len - 1;
      const last = this.tiles[lastIdx];
      if (last && last.state === 'configuring') {
        this.selectedIndex = lastIdx;
      }
    },
    // Re-seed the draft whenever the stage lands on a different tile awaiting
    // configuration (including one the parent just added and auto-selected).
    selectedTile: {
      immediate: true,
      handler(tile) {
        if (!tile || tile.state !== 'configuring') {
          this.configRowIndex = null;
          this.configOpen = false;
          return;
        }
        if (this.configRowIndex === tile.rowIndex) return;
        this.configRowIndex = tile.rowIndex;
        this.configOpen = true;
        this.configDirection = tile.userInstructions || '';
        this.configOverlayText = tile.overlayText || '';
        this.configOverlayMode = tile.overlayTextMode || 'editable';
        this.configRefs = (tile.referenceImageUrls || []).filter(Boolean);
        // The packshots the add step copied from the matching slot / first slot.
        this.configProducts = ((tile.productImageUrls && tile.productImageUrls.length)
          ? tile.productImageUrls
          : [tile.productImageUrl]).filter(Boolean);
        this.configLogo = tile.brandLogoUrl || null;
        this.configTab = 'products';
      },
    },
    // The parent resolved a File Browser pick onto the tile — mirror it into the
    // open draft so the thumbnail shows without losing typed text.
    'selectedTile.referenceImageUrls': function onTileRefsChanged(refs) {
      const tile = this.selectedTile;
      if (!tile || tile.state !== 'configuring' || this.configRowIndex !== tile.rowIndex) return;
      this.configRefs = (refs || []).filter(Boolean);
    },
    'selectedTile.productImageUrls': function onTileProductsChanged(urls) {
      const tile = this.selectedTile;
      if (!tile || tile.state !== 'configuring' || this.configRowIndex !== tile.rowIndex) return;
      this.configProducts = (urls || []).filter(Boolean);
    },
    'selectedTile.brandLogoUrl': function onTileLogoChanged(url) {
      const tile = this.selectedTile;
      if (!tile || tile.state !== 'configuring' || this.configRowIndex !== tile.rowIndex) return;
      this.configLogo = url || null;
    },
  },
  mounted() {
    document.addEventListener('mousedown', this.onDocumentMousedown);
    document.addEventListener('keydown', this.onConfigureKeydown);
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.onDocumentMousedown);
    document.removeEventListener('keydown', this.onConfigureKeydown);
    document.removeEventListener('keydown', this.onLightboxKeydown);
  },
  methods: {
    // Emit the raw action string. AIChatMessage forwards it verbatim through
    // onWidgetAction(widget.id, $event) — the same `@action` convention used by
    // the other single-action widgets (action_buttons, template_slider, etc.).
    act(action) {
      this.$emit('action', action);
    },
    selectTile(index) {
      this.selectedIndex = index;
      // Re-opening a tile that is still waiting to be set up shows its panel again.
      const tile = this.tiles[index];
      if (tile && tile.state === 'configuring') {
        this.configOpen = true;
      }
    },
    addType(key) {
      this.addableTypesOpen = false;
      this.act(`pdp_grid_add_type:${key}`);
    },
    // ── Configure panel for a newly added tile (POR-13766-6) ──────────────────
    // Both image slots ("products" = the packshots the AI preserves, "refs" = the
    // style to match) share these helpers; `slot` picks which list to touch.
    configList(slot) {
      if (slot === 'products') return this.configProducts;
      // The brand logo is a single image, modelled as a 0..1 list so it can share
      // the upload / paste / drop helpers.
      if (slot === 'logo') return this.configLogo ? [this.configLogo] : [];
      return this.configRefs;
    },
    setConfigList(slot, list) {
      if (slot === 'products') {
        this.configProducts = list;
      } else if (slot === 'logo') {
        this.configLogo = list.length ? list[list.length - 1] : null;
      } else {
        this.configRefs = list;
      }
    },
    removeConfigImage(slot, idx) {
      this.setConfigList(slot, this.configList(slot).filter((url, i) => i !== idx));
    },
    readImageFileAsDataUri(file) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          const uri = reader.result;
          resolve((typeof uri === 'string' && uri.startsWith('data:')) ? uri : null);
        };
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(file);
      });
    },
    async addConfigImageFiles(slot, files) {
      const images = (files || []).filter((f) => f && String(f.type || '').startsWith('image/'));
      if (!images.length) return;
      const uris = (await Promise.all(images.map((f) => this.readImageFileAsDataUri(f)))).filter(Boolean);
      const current = this.configList(slot);
      const fresh = uris.filter((u) => !current.includes(u));
      if (fresh.length) this.setConfigList(slot, [...current, ...fresh]);
    },
    pickConfigImagesFromComputer(slot) {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.multiple = true;
      input.style.display = 'none';
      input.onchange = async () => {
        const files = Array.from(input.files || []);
        if (input.parentNode) input.parentNode.removeChild(input);
        await this.addConfigImageFiles(slot, files);
      };
      document.body.appendChild(input);
      input.click();
    },
    async pasteConfigImage(slot) {
      try {
        if (!navigator.clipboard || !navigator.clipboard.read) return;
        const items = await navigator.clipboard.read();
        for (const item of items) {
          const type = (item.types || []).find((t) => t.indexOf('image') === 0);
          if (type) {
            const blob = await item.getType(type);
            await this.addConfigImageFiles(slot, [new File([blob], 'Pasted image', { type })]);
            return;
          }
        }
      } catch (e) {
        // Non-secure context or no clipboard permission — Upload still works.
      }
    },
    onConfigDragOver(slot, event) {
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
      this.configDragSlot = slot;
    },
    onConfigDragLeave(event) {
      if (!event.currentTarget || !event.currentTarget.contains(event.relatedTarget)) {
        this.configDragSlot = null;
      }
    },
    async onConfigDrop(slot, event) {
      this.configDragSlot = null;
      await this.addConfigImageFiles(slot, Array.from((event.dataTransfer && event.dataTransfer.files) || []));
    },
    onConfigureKeydown(event) {
      if (event.key === 'Escape' && this.configOpen) {
        this.configOpen = false;
      }
    },
    removeConfiguredTile() {
      const tile = this.configuringTile;
      if (!tile) return;
      this.configOpen = false;
      this.act(`pdp_grid_skip:${tile.rowIndex}`);
    },
    generateConfigured() {
      const tile = this.configuringTile;
      if (!tile) return;
      this.configOpen = false;
      this.act(`pdp_grid_configure_generate:${JSON.stringify({
        rowIndex: tile.rowIndex,
        userInstructions: (this.configDirection || '').trim() || null,
        overlayText: (this.configOverlayText || '').trim() || null,
        overlayTextMode: this.configOverlayMode || 'editable',
        referenceImageUrls: this.configRefs,
        productImageUrls: this.configProducts,
        brandLogoUrl: this.configLogo || null,
      })}`);
    },
    toggleAddDropdown() {
      this.addableTypesOpen = !this.addableTypesOpen;

      if (this.addableTypesOpen) {
        this.$nextTick(this.updateDropdownPosition);
      }
    },
    updateDropdownPosition() {
      const trigger = this.$refs.addTrigger;

      if (!trigger) {
        return;
      }

      const rect = trigger.getBoundingClientRect();
      const estimatedHeight = 240;
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;

      this.dropdownOpensDown = spaceAbove < estimatedHeight && spaceBelow > spaceAbove;
    },
    onDocumentMousedown(event) {
      if (!this.addableTypesOpen) {
        return;
      }

      const inside = event.target.closest && event.target.closest('.pdp-grid__thumb--add');

      if (!inside) {
        this.addableTypesOpen = false;
      }
    },
    // Use-as-is tiles (the original packshot or the user's own upload) are not
    // AI-generated, so they must not carry the "✦ AI" badge (POR-13366).
    isUseAsIs(tile) {
      return tile.mode === 'original' || tile.mode === 'yours';
    },
    badgeText(tile) {
      if (tile.mode === 'gallery') return 'Gallery';
      if (tile.mode === 'original') return 'Original';
      if (tile.mode === 'yours') return 'Yours';
      return '✦ AI';
    },
    // Layered vs Static (product-image) toggle, shown for AI Lifestyle tiles.
    // Switching the mode REGENERATES the tile (separate = product-free background
    // so the product can be layered without duplicating it). Scene tiles are
    // excluded: composeSceneTileUrl always bakes the product into the composite,
    // so they can't be layered without a duplicate — they stay Static. Info
    // panels (noOverlay) and separate-default hero tiles are also excluded. (POR-13271)
    canToggleProductMode(tile) {
      if (!tile || tile.state !== 'done') return false;
      if (tile.mode !== 'ai') return false;
      const def = BUILT_IN_TYPES[tile.type];
      if (def && def.noOverlay) return false;
      return tile.type === 'lifestyle';
    },
    tileProductMode(tile) {
      return (tile.context && tile.context.productMode) || 'integrated';
    },
    openTile(tile) {
      if (tile && tile.state === 'done' && tile.url) {
        this.lightboxTile = tile;
        document.addEventListener('keydown', this.onLightboxKeydown);
      }
    },
    closeLightbox() {
      this.lightboxTile = null;
      document.removeEventListener('keydown', this.onLightboxKeydown);
    },
    // Step to the previous/next openable tile without leaving the lightbox.
    // hasPrev/hasNext guard the boundaries (arrows are hidden at the ends). (POR-13637)
    showPrev() {
      if (this.hasPrev) this.lightboxTile = this.openableTiles[this.lightboxIndex - 1];
    },
    showNext() {
      if (this.hasNext) this.lightboxTile = this.openableTiles[this.lightboxIndex + 1];
    },
    onLightboxKeydown(e) {
      if (!this.lightboxTile) return;

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.showPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.showNext();
      } else if (e.key === 'Escape') {
        this.closeLightbox();
      }
    },
    // Download the previewed tile. Mirrors AIChatMessage.onDownloadAttachment so
    // the saved file matches what the lightbox shows: when the tile has a product
    // overlay (separate/layered mode) or Cambridge callouts, composite them onto
    // the base so the download isn't a blank white box; otherwise download the
    // image directly. (POR-13627)
    async downloadTile(tile) {
      if (!tile || !tile.url) return;
      const baseName = (tile.label || 'ai-studio-image').toString().trim().replace(/\s+/g, '-').toLowerCase();
      const fileName = `${baseName || 'ai-studio-image'}.png`;
      const hasOverlay = !!tile.overlayUrl;
      const hasCallouts = !!(tile._mrhiCallouts && tile._mrhiCallouts.type === 'cambridge');
      if (hasOverlay || hasCallouts) {
        await downloadAttachmentComposite(
          { payload: tile.url, overlayUrl: tile.overlayUrl, _mrhiCallouts: tile._mrhiCallouts },
          fileName,
        );
      } else {
        await downloadFileFromUrl(tile.url, fileName);
      }
    },
    downloadLightboxImage() {
      return this.downloadTile(this.lightboxTile);
    },
  },
};
</script>

<style scoped>
/* Transparent + zero padding so the widget blends into the assistant chat
   bubble (background #F2F5F9) as a single container instead of a nested green
   box, and spans the full bubble width. (POR-13183 / POR-13766) */
.pdp-grid { background: transparent; padding: 0; }
.pdp-grid__header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; gap: 8px; }
.pdp-grid__count { font-size: 13px; color: #2b3a36; }
.pdp-grid__generating { color: #6b7c77; }
.pdp-grid__actions { display: flex; gap: 8px; align-items: center; }
.pdp-grid__stop { display: inline-flex; align-items: center; gap: 4px; background: none; border: none; color: #d9534f; font-weight: 600; font-size: 13px; cursor: pointer; padding: 4px 6px; }
.pdp-grid__stop i { font-size: 12px; }
.pdp-grid__stop:hover { color: #c9302c; }
.pdp-grid__btn { background: #fff; border: 1px solid #cdd6d3; border-radius: 8px; padding: 6px 12px; font-size: 13px; cursor: pointer; }
.pdp-grid__btn--primary { background: #1f8a6d; border-color: #1f8a6d; color: #fff; }
.pdp-grid__btn:disabled { opacity: .5; cursor: default; }

/* Viewer: large main preview + a thumbnail filmstrip below. */
.pdp-grid__viewer { display: flex; flex-direction: column; gap: 12px; }
.pdp-grid__stage {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 0.62;
  min-height: 320px;
  max-height: 62vh;
  background: #dfe8e5;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.pdp-grid__stage--generating, .pdp-grid__stage--queued, .pdp-grid__stage--regenerating { background: #d6e2de; }
.pdp-grid__badge { position: absolute; top: 10px; right: 10px; z-index: 2; background: #1f8a6d; color: #fff; font-size: 12px; border-radius: 6px; padding: 3px 8px; }
.pdp-grid__badge--gallery { background: #8a97a0; }
.pdp-grid__badge--plain { background: #8a97a0; }
.pdp-grid__stage-img { max-width: 100%; max-height: 100%; width: auto; height: auto; object-fit: contain; cursor: pointer; }
.pdp-grid__stage-overlay { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); max-width: 70%; max-height: 70%; width: auto; height: auto; object-fit: contain; pointer-events: none; }
.pdp-grid__placeholder { display: flex; flex-direction: column; align-items: center; gap: 8px; color: #2b3a36; }
.pdp-grid__placeholder .bi-stars { font-size: 40px; color: #1f8a6d; }
.pdp-grid__spark--active { animation: spin 1.4s linear infinite; }
.pdp-grid__spark--queued { opacity: 0.4; }
.pdp-grid__stage-label { font-size: 14px; text-align: center; padding: 0 8px; line-height: 1.3; }
.pdp-grid__stage-state { font-size: 12px; color: #6b7c77; }
.pdp-grid__stage-error { position: absolute; bottom: 14px; left: 0; right: 0; display: flex; align-items: center; justify-content: center; gap: 10px; font-size: 13px; color: #6b7c77; }

/* Per-image toolbar on the stage, for the selected tile — revealed on hover
   (download bottom-left, mode toggle + regenerate/remove bottom-right). (POR-13766) */
.pdp-grid__stage-tools { position: absolute; left: 12px; right: 12px; bottom: 12px; display: flex; align-items: flex-end; justify-content: space-between; gap: 8px; pointer-events: none; opacity: 0; transition: opacity 0.15s; }
.pdp-grid__stage-tools > * { pointer-events: none; }
.pdp-grid__stage:hover .pdp-grid__stage-tools { opacity: 1; }
.pdp-grid__stage:hover .pdp-grid__stage-tools > * { pointer-events: auto; }
.pdp-grid__stage-download { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; background: rgba(255, 255, 255, 0.95); border: 1px solid #cdd6d3; border-radius: 7px; color: #334155; font-size: 14px; cursor: pointer; }
.pdp-grid__stage-download:hover { border-color: #94a3b8; color: #1a1a2e; }
.pdp-grid__stage-actions { display: flex; align-items: flex-end; gap: 6px; margin-left: auto; }
.pdp-grid__stage-btn { display: inline-flex; align-items: center; gap: 5px; background: rgba(255, 255, 255, 0.95); border: 1px solid #cdd6d3; border-radius: 7px; padding: 5px 10px; font-size: 12px; line-height: 1.2; cursor: pointer; color: #334155; }
.pdp-grid__stage-btn:hover { border-color: #94a3b8; color: #1a1a2e; }
.pdp-grid__stage-btn i { font-size: 12px; }
.pdp-grid__stage-btn--icon { padding: 5px 8px; }
.pdp-grid__mode { display: flex; gap: 4px; }
.pdp-grid__mode-btn { background: rgba(255, 255, 255, 0.95); border: 1px solid #cdd6d3; border-radius: 7px; padding: 4px 10px; font-size: 12px; line-height: 1.3; cursor: pointer; color: #475569; }
.pdp-grid__mode-btn.is-active { background: #379C75; border-color: #379C75; color: #fff; }
.pdp-grid__mode-btn:hover:not(.is-active) { border-color: #94a3b8; color: #1a1a2e; }

/* Thumbnail filmstrip. The thumbs scroll inside `__strip-scroll`; the "Add" tile
   sits OUTSIDE that overflow container so its dropdown anchors to it and isn't
   clipped by the horizontal scroll. (POR-13766) */
.pdp-grid__strip { display: flex; align-items: flex-start; gap: 10px; padding: 2px; }
.pdp-grid__strip-scroll { display: flex; gap: 10px; overflow-x: auto; flex: 1 1 auto; min-width: 0; scrollbar-width: thin; padding-bottom: 2px; }
.pdp-grid__thumb { flex: 0 0 auto; width: 74px; display: flex; flex-direction: column; align-items: center; gap: 5px; background: none; border: none; padding: 0; cursor: pointer; }
.pdp-grid__thumb-box { position: relative; width: 74px; height: 74px; border-radius: 8px; border: 2px solid transparent; background: #e8efec; overflow: hidden; display: flex; align-items: center; justify-content: center; }
.pdp-grid__thumb.is-selected .pdp-grid__thumb-box { border-color: #1f8a6d; }
.pdp-grid__thumb-img { width: 100%; height: 100%; object-fit: cover; }
.pdp-grid__thumb-overlay { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); max-width: 84%; max-height: 84%; width: auto; height: auto; object-fit: contain; pointer-events: none; }
.pdp-grid__thumb-spinner { width: 20px; height: 20px; border-radius: 50%; border: 2px solid #c3d2cd; border-top-color: #1f8a6d; animation: spin 0.9s linear infinite; }
.pdp-grid__thumb-error { font-size: 18px; color: #d9534f; }
.pdp-grid__thumb-configuring { font-size: 18px; color: #1f8a6d; }
.pdp-grid__thumb-box--configuring { border: 2px dashed #1f8a6d; background: #eef6f3; }
.pdp-grid__thumb-skipped { font-size: 10px; color: #6b7c77; }
.pdp-grid__thumb-label { font-size: 11px; color: #475569; line-height: 1.2; text-align: center; max-width: 74px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pdp-grid__thumb--add { position: relative; flex: 0 0 auto; cursor: default; }
.pdp-grid__add-trigger { border: 2px dashed #cdd6d3; color: #475569; font-size: 22px; cursor: pointer; }
.pdp-grid__add-trigger:hover, .pdp-grid__add-trigger.is-open { border-color: #1f8a6d; color: #1f8a6d; }
.pdp-grid__add-dropdown-menu {
  position: absolute;
  bottom: calc(100% + 6px);
  right: 0;
  min-width: 180px;
  max-height: fit-content;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
  overflow-y: auto;
  z-index: 5;
  padding: 4px;
}
.pdp-grid__add-dropdown-menu--down { top: calc(100% + 6px); bottom: auto; }
.pdp-grid__add-dropdown-header {
  padding: 7px 12px 4px;
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.pdp-grid__add-item {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 7px 12px;
  background: transparent;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 13px;
  color: #334155;
  text-align: left;
  white-space: nowrap;
}
.pdp-grid__add-item i { font-size: 11px; color: #94a3b8; }
.pdp-grid__add-item:hover { background: #f1f5f9; }

/* Cambridge MRHI callouts: two black rectangles on the right edge — the flavor/variant
   (rotated 90°) in the top box and the count/size in the bottom box. (POR-13612) */
.pdp-grid__callouts { position: absolute; inset: 0; pointer-events: none; z-index: 3; }
.pdp-grid__callouts::before,
.pdp-grid__callouts::after { content: ''; position: absolute; right: 0; width: 22%; background: #000; z-index: 0; }
.pdp-grid__callouts::before { top: 0; height: 73%; }
.pdp-grid__callouts::after { top: 78%; height: 22%; }
.pdp-grid__callout-flavor,
.pdp-grid__callout-count { position: absolute; left: 89%; color: #fff; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; white-space: nowrap; z-index: 1; }
.pdp-grid__callout-flavor { top: 36.5%; transform: translate(-50%, -50%) rotate(-90deg); font-size: 16px; }
.pdp-grid__callout-count { top: 89%; transform: translate(-50%, -50%); font-size: 11px; text-align: center; }

.spin { animation: spin 1s linear infinite; }
.pdp-grid__lightbox { position: fixed; inset: 0; z-index: 2000; background: rgba(17,24,39,0.72); display: flex; align-items: center; justify-content: center; }
.pdp-grid__lightbox-stage { position: relative; display: inline-block; line-height: 0; background: #fff; border-radius: 8px; }
.pdp-grid__lightbox-img { display: block; max-width: 88vw; max-height: 88vh; width: auto; height: auto; }
.pdp-grid__lightbox-overlay { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); max-width: 86%; max-height: 86%; width: auto; height: auto; object-fit: contain; }
.pdp-grid__lightbox-close { position: absolute; top: 18px; right: 22px; z-index: 1; background: rgba(255,255,255,0.9); border: none; border-radius: 50%; width: 36px; height: 36px; font-size: 16px; cursor: pointer; color: #111827; }
.pdp-grid__lightbox-download { position: absolute; top: 18px; right: 66px; z-index: 1; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.9); border: none; border-radius: 50%; width: 36px; height: 36px; font-size: 15px; cursor: pointer; color: #111827; }
.pdp-grid__lightbox-download:hover { background: #fff; }
/* Prev/next arrows on the left/right edges — hidden at the ends via v-if. (POR-13637) */
.pdp-grid__lightbox-nav { position: absolute; top: 50%; transform: translateY(-50%); z-index: 1; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.9); border: none; border-radius: 50%; width: 44px; height: 44px; font-size: 20px; cursor: pointer; color: #111827; }
.pdp-grid__lightbox-nav:hover { background: #fff; }
.pdp-grid__lightbox-nav i { display: block; line-height: 1; }
.pdp-grid__lightbox-nav--prev { left: 22px; }
.pdp-grid__lightbox-nav--next { right: 22px; }
/* Cambridge MRHI callouts in the enlarged lightbox (larger type than the tile). (POR-13612) */
.pdp-grid__lightbox-callouts { position: absolute; inset: 0; pointer-events: none; z-index: 2; }
.pdp-grid__lightbox-callouts::before,
.pdp-grid__lightbox-callouts::after { content: ''; position: absolute; right: 0; width: 22%; background: #000; z-index: 0; }
.pdp-grid__lightbox-callouts::before { top: 0; height: 73%; }
.pdp-grid__lightbox-callouts::after { top: 78%; height: 22%; }
.pdp-grid__lightbox-callout-flavor,
.pdp-grid__lightbox-callout-count { position: absolute; left: 89%; color: #fff; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; white-space: nowrap; z-index: 1; }
.pdp-grid__lightbox-callout-flavor { top: 36.5%; transform: translate(-50%, -50%) rotate(-90deg); font-size: 32px; }
.pdp-grid__lightbox-callout-count { top: 89%; transform: translate(-50%, -50%); font-size: 22px; text-align: center; }
@keyframes spin { to { transform: rotate(360deg); } }
/* Configure panel for a newly added tile (POR-13766-6). Sits inside the stage so
   the set stays in view while the user sets up the new image. */
.pdp-grid__stage--configuring { background: #f1f6f4; }
/* Placeholder shown in the stage while the panel is dismissed. */
.pdp-grid__configure-cta {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: #4a6a60;
}
.pdp-grid__configure-cta .bi { font-size: 34px; color: #1f8a6d; }
/* The panel floats ABOVE the widget instead of living inside the fixed-ratio
   image stage, which clipped it and forced an awkward inner scroll. (POR-13766-6) */
.pdp-grid__configure-overlay {
  position: fixed;
  inset: 0;
  /* Above the chat, but BELOW the File Browser modal (backdrop 1040 / modal 1050)
     so "Choose from Files" isn't hidden behind this panel. (POR-13766-6) */
  z-index: 1030;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.pdp-grid__configure {
  width: 100%;
  max-width: 560px;
  max-height: 86vh;
  overflow-y: auto;
  margin: 0 auto;
  padding: 16px 18px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
  text-align: left;
}
.pdp-grid__configure-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}
.pdp-grid__configure-title { font-size: 13px; font-weight: 700; color: #1a1a2e; }
.pdp-grid__configure-title .bi { color: #1f8a6d; margin-right: 4px; }
.pdp-grid__configure-optional { font-size: 10px; color: #64748b; margin-left: auto; }
.pdp-grid__configure-close {
  background: none;
  border: none;
  padding: 0 0 0 10px;
  color: #94a3b8;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
}
.pdp-grid__configure-close:hover { color: #334155; }
.pdp-grid__configure-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 10px 0 6px;
}
.pdp-grid__configure-label .bi { color: #64748b; margin-right: 3px; }
.pdp-grid__configure-text {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 13px;
  color: #334155;
  background: #f8fafc;
  font-family: inherit;
  resize: vertical;
}
.pdp-grid__configure-text:focus { outline: none; border-color: #189a73; }
.pdp-grid__configure-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.pdp-grid__configure-modes { display: flex; gap: 4px; }
.pdp-grid__configure-mode {
  padding: 3px 9px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #f8fafc;
  color: #475569;
  font-size: 11px;
  cursor: pointer;
}
.pdp-grid__configure-mode.is-active { background: #1f8a6d; border-color: #1f8a6d; color: #fff; }
/* Tab strip for the three image slots, matching the row picker's Customize
   popover so the two panels read the same. (POR-13766-6) */
.pdp-grid__configure-tabs {
  display: flex;
  gap: 4px;
  margin: 14px 0 8px;
  padding: 3px;
  background: #f1f5f9;
  border-radius: 8px;
}
.pdp-grid__configure-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  justify-content: center;
  padding: 6px 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.pdp-grid__configure-tab:hover { color: #334155; }
.pdp-grid__configure-tab.is-active {
  background: #fff;
  color: #1a1a2e;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12);
}
.pdp-grid__configure-tab-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #22c55e;
  flex-shrink: 0;
}
.pdp-grid__configure-refs { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 8px; }
.pdp-grid__configure-ref { position: relative; width: 56px; height: 56px; }
.pdp-grid__configure-ref img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}
.pdp-grid__configure-ref-remove {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: none;
  background: rgba(15, 23, 42, 0.75);
  color: #fff;
  font-size: 8px;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.pdp-grid__configure-drop {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 9px 10px;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  background: #f8fafc;
}
.pdp-grid__configure-drop.is-dragover { border-color: #1f8a6d; background: #eef6f3; }
.pdp-grid__configure-link {
  background: none;
  border: none;
  padding: 0;
  color: #189a73;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
}
.pdp-grid__configure-link:hover { text-decoration: underline; }
.pdp-grid__configure-sep { color: #cbd5e1; }
.pdp-grid__configure-foot { display: flex; align-items: center; gap: 12px; margin-top: 14px; }
.pdp-grid__configure-generate {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #1f8a6d;
  border: none;
  border-radius: 8px;
  padding: 8px 14px;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.pdp-grid__configure-generate:hover { background: #18795e; }
.pdp-grid__configure-remove {
  background: none;
  border: none;
  padding: 0;
  color: #64748b;
  font-size: 12.5px;
  cursor: pointer;
}
.pdp-grid__configure-remove:hover { color: #b91c1c; }

@media (max-width: 640px) { .pdp-grid__stage { min-height: 240px; } }
</style>
